import React, { useEffect, useMemo, useRef, useState } from "react";
import { EmptyStateErro, EmptyStateSemPlanilha } from "../../components/EmptyState";
import { FiArrowLeft, FiInfo, FiUsers } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import BotaoExportacao from "../../components/Exportacao";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import FilterPanel from "../../components/FilterPanel";
import {
  BuscaInput,
  FilterField,
  FilterGrid,
  FilterLabel,
  FiltroSelect,
} from "../../components/PainelTabela/styles";
import { useDados } from "../../context/DadosContext";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { buildMarceneiros } from "../../utils/marceneiros";
import {
  Aba,
  Abas,
  BackButton,
  Badge,
  CardLabel,
  CardSub,
  CardValor,
  CardWrapper,
  CoverageGrid,
  CoverageItem,
  CoverageTitle,
  DetailMeta,
  DetailTitleBlock,
  EmptyText,
  Grid,
  InlineBar,
  InlineBarCell,
  InfoButton,
  KpiPopover,
  KpiPopoverExample,
  KpiPopoverText,
  KpiPopoverTitle,
  KpiList,
  KpiPill,
  LinkButton,
  ScoreCell,
  ScoreFill,
  ScoreTrack,
  Secao,
  SecaoTitulo,
  Tabela,
  TabelaWrapper,
  Td,
  Th,
  Titulo,
  TopBar,
  TopBarActions,
  Tr,
  Wrapper,
} from "./styles";

const EMPTY_VALUE = "\u2014";
const INITIAL_FILTERS = {
  busca: "",
  situacao: "TODOS",
};

const KPI_COVERAGE = [
  {
    title: "Monitoráveis agora",
    tone: "success",
    items: [
      "Lead time de marcenaria",
      "Tempo de fila",
      "OTD interno",
      "Frequência por tipo",
      "Lead time por tipo",
      "Troca de contexto",
    ],
  },
  {
    title: "Com proxy operacional",
    tone: "warning",
    items: [
      "Produtividade ajustada",
      "ABC esforço x receita",
      "Receita por dia produtivo",
      "Dependência crítica",
    ],
  },
  {
    title: "Pedem novo apontamento",
    tone: "neutral",
    items: [
      "FPY",
      "Horas de retrabalho",
      "Eficiência vs tempo padrão",
      "Custo real",
      "Margem-hora",
      "Throughput/hora",
      "Precisão do orçamento",
    ],
  },
];

const ABAS_DETALHE = [
  { id: "desempenho", label: "Desempenho" },
  { id: "tipos", label: "Tipos de Ambiente" },
  { id: "economico", label: "Econômico e Fluxo" },
  { id: "ambientes", label: "Ambientes" },
];

const KPI_HELP = {
  Marceneiros: {
    description: "Quantidade de profissionais com pelo menos um ambiente atribuído no Gerenciador de Produção.",
    example: "Exemplo: se Eliel, Edjair e Cristiano aparecem como responsáveis, o painel conta 3 marceneiros.",
  },
  "Ambientes atribuidos": {
    description: "Total de ambientes que possuem marceneiro responsável preenchido.",
    example: "Exemplo: uma cozinha e dois quartos vinculados ao mesmo marceneiro contam como 3 ambientes atribuídos.",
  },
  Concluidos: {
    description: "Ambientes com data de término de marcenaria registrada.",
    example: "Exemplo: se a marcenaria terminou em 30/04, esse ambiente entra como concluído.",
  },
  "OTD medio": {
    description: "Percentual de ambientes concluídos dentro do prazo de marcenaria.",
    example: "Exemplo: 8 ambientes concluídos no prazo em 10 finalizados resultam em OTD de 80%.",
  },
  "Lead time medio": {
    description: "Média de dias úteis de produção entre o início e o término da marcenaria, descontando bloqueios registrados.",
    example: "Exemplo: iniciou na segunda e terminou na sexta, mas ficou bloqueado por 2 dias; o lead time produtivo fica em 3 dias úteis.",
  },
  "Sem marceneiro": {
    description: "Ambientes ativos que ainda não possuem marceneiro responsável informado.",
    example: "Exemplo: se um ambiente já chegou ao fluxo produtivo, mas o campo Marceneiro Responsável está vazio, ele entra aqui.",
  },
  "Fila média antes da marcenaria": {
    description: "Média de dias úteis entre a etapa anterior mais recente e o início da marcenaria.",
    example: "Exemplo: bordo finalizou na segunda e marcenaria iniciou na quinta; a fila considerada é de 3 dias úteis menos o dia de início.",
  },
  "OTD interno": {
    description: "Entrega no prazo dentro da etapa de marcenaria, por marceneiro.",
    example: "Exemplo: se 6 de 8 ambientes do marceneiro terminaram até a data limite, o OTD interno fica em 75%.",
  },
  "Produtividade ajustada": {
    description: "Proxy de produtividade calculado por pontos equivalentes de esforço por dia útil de marcenaria.",
    example: "Exemplo: um ambiente com 24 pontos equivalentes concluído em 3 dias gera 8 pts/du.",
  },
  "Score monitoravel": {
    description: "Nota sintética baseada nos KPIs que a planilha já permite medir: prazo, produtividade proxy e disciplina de apontamento.",
    example: "Exemplo: bom OTD, boa produtividade e datas bem preenchidas elevam o score mesmo sem dados de retrabalho.",
  },
  "Troca de contexto": {
    description: "Percentual de ambientes iniciados que não foram concluídos na mesma semana.",
    example: "Exemplo: se o marceneiro inicia uma cozinha na semana 1 e termina na semana 2, esse ambiente conta como troca de contexto.",
  },
  "Disciplina de apontamento": {
    description: "Percentual de ambientes com datas essenciais preenchidas para leitura confiável dos KPIs.",
    example: "Exemplo: ambiente com início, prazo e término registrados melhora a disciplina de apontamento.",
  },
  "Receita atribuida": {
    description: "Soma do valor dos ambientes vinculados ao marceneiro.",
    example: "Exemplo: dois ambientes de R$ 10 mil e R$ 15 mil resultam em R$ 25 mil de receita atribuída.",
  },
  "Receita por dia produtivo": {
    description: "Proxy econômico que divide a receita dos ambientes concluídos pelos dias úteis consumidos na marcenaria.",
    example: "Exemplo: R$ 40 mil concluídos em 8 dias de marcenaria geram R$ 5 mil por dia produtivo.",
  },
  "Dependencia em alto valor": {
    description: "Quanto dos ambientes de maior valor está concentrado nesse marceneiro.",
    example: "Exemplo: se existem 20 ambientes acima da média de valor e 5 estão com o mesmo marceneiro, a dependência é 25%.",
  },
  "Ambientes alto valor atrasados": {
    description: "Quantidade de ambientes acima da média de valor que ainda estão abertos e com prazo vencido.",
    example: "Exemplo: uma suite de alto valor com prazo de marcenaria vencido e sem término registrado entra nessa conta.",
  },
  "Esforço equivalente": {
    description: "Estimativa de esforço baseada em m2, peças, usinagens, caixaria e complexidade do ambiente.",
    example: "Exemplo: um closet com muitas peças e maior complexidade recebe mais pontos que um painel simples.",
  },
};

function formatNumber(value, decimals = 1) {
  if (value == null || !Number.isFinite(Number(value))) {
    return EMPTY_VALUE;
  }

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals > 0 ? 1 : 0,
  }).format(Number(value));
}

function formatPercent(value) {
  return value == null ? EMPTY_VALUE : `${formatNumber(value, 1)}%`;
}

function formatDays(value) {
  return value == null ? EMPTY_VALUE : `${formatNumber(value, 1)} du`;
}

function formatEffort(value) {
  return value == null ? EMPTY_VALUE : `${formatNumber(value, 1)} pts`;
}

function formatProductivity(value) {
  return value == null ? EMPTY_VALUE : `${formatNumber(value, 2)} pts/du`;
}

function getPercentTone(value) {
  if (value == null) return "neutral";
  if (value < 70) return "danger";
  if (value < 85) return "warning";
  return "success";
}

function getScoreTone(value) {
  if (value == null) return "neutral";
  if (value < 65) return "danger";
  if (value < 82) return "warning";
  return "success";
}

function getSituacaoMarceneiro(marceneiro) {
  if (marceneiro.atrasadosAbertos > 0) {
    return "Com atraso";
  }

  if (marceneiro.emAndamento > 0) {
    return "Em execução";
  }

  if (marceneiro.aguardandoInicio > 0) {
    return "A iniciar";
  }

  return "Concluído";
}

function getSituacaoTone(situacao) {
  if (situacao === "Com atraso") return "danger";
  if (situacao === "Em execução") return "warning";
  if (situacao === "A iniciar") return "info";
  return "success";
}

function getAmbienteStatus(metric) {
  if (metric.completed && metric.onTime) {
    return { label: "Concluído no prazo", tone: "success" };
  }

  if (metric.completed) {
    return { label: "Concluído com atraso", tone: "danger" };
  }

  if (metric.lateOpen) {
    return { label: "Atrasado", tone: "danger" };
  }

  if (metric.started) {
    return { label: "Em execução", tone: "warning" };
  }

  return { label: "A iniciar", tone: "info" };
}

function getAbcGap(row) {
  if (row.esforcoClass === "A" && row.valorClass !== "A") {
    return { label: "Esforço alto sem receita A", tone: "danger" };
  }

  if (row.valorClass === "A" && row.esforcoClass !== "A") {
    return { label: "Boa alavanca de receita", tone: "success" };
  }

  return { label: "Equilibrado", tone: "neutral" };
}

function KPICard({ label, valor, sub, tone = "default", delay = 0, info }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const cardRef = useRef(null);
  const help = info || KPI_HELP[label];

  useEffect(() => {
    if (!infoOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setInfoOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [infoOpen]);

  return (
    <CardWrapper
      ref={cardRef}
      $tone={tone}
      $infoOpen={infoOpen}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.22 }}
    >
      {help && (
        <>
          <InfoButton
            type="button"
            $active={infoOpen}
            aria-label={`Ver explicação do KPI ${label}`}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((current) => !current)}
          >
            <FiInfo size={14} />
          </InfoButton>
          {infoOpen && (
            <KpiPopover role="dialog" aria-label={`Explicação do KPI ${label}`}>
              <KpiPopoverTitle>{label}</KpiPopoverTitle>
              <KpiPopoverText>{help.description}</KpiPopoverText>
              <KpiPopoverExample>{help.example}</KpiPopoverExample>
            </KpiPopover>
          )}
        </>
      )}
      <CardLabel>{label}</CardLabel>
      <CardValor $tone={tone}>{valor}</CardValor>
      {sub && <CardSub>{sub}</CardSub>}
    </CardWrapper>
  );
}

function ScoreBar({ value }) {
  const safeValue = Math.max(0, Math.min(Number(value) || 0, 100));
  const tone = getScoreTone(value);

  return (
    <ScoreCell>
      <strong>{value == null ? EMPTY_VALUE : `${formatNumber(value, 0)}%`}</strong>
      <ScoreTrack>
        <ScoreFill $width={`${safeValue}%`} $tone={tone} />
      </ScoreTrack>
    </ScoreCell>
  );
}

function CoveragePanel() {
  return (
    <Secao data-pdf="cobertura-kpis">
      <SecaoTitulo>Cobertura dos KPIs no Gerenciador de Produção</SecaoTitulo>
      <CoverageGrid>
        {KPI_COVERAGE.map((group) => (
          <CoverageItem key={group.title} $tone={group.tone}>
            <CoverageTitle $tone={group.tone}>{group.title}</CoverageTitle>
            <KpiList>
              {group.items.map((item) => (
                <KpiPill key={item}>{item}</KpiPill>
              ))}
            </KpiList>
          </CoverageItem>
        ))}
      </CoverageGrid>
    </Secao>
  );
}

function MarceneirosList({ analysis }) {
  const navigate = useNavigate();
  const { precosVisiveis } = usePrecosVisiveis();
  const pageRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });

  const filteredMarceneiros = useMemo(
    () =>
      analysis.marceneiros.filter((marceneiro) => {
        const query = filters.busca.trim().toLowerCase();
        const situacao = getSituacaoMarceneiro(marceneiro);

        if (
          query &&
          ![marceneiro.nome, situacao]
            .join(" ")
            .toLowerCase()
            .includes(query)
        ) {
          return false;
        }

        if (filters.situacao !== "TODOS" && situacao !== filters.situacao) {
          return false;
        }

        return true;
      }),
    [analysis.marceneiros, filters]
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.busca.trim()) chips.push(`Busca: ${filters.busca.trim()}`);
    if (filters.situacao !== "TODOS") chips.push(`Situação: ${filters.situacao}`);
    return chips;
  }, [filters]);

  const exportResumo = [
    { label: "Marceneiros",        value: analysis.marceneiros.length, type: "number" },
    { label: "Ambientes atribuídos", value: analysis.global.ambientes, type: "number" },
    { label: "OTD médio",          value: analysis.global.otd,         type: "percentage" },
    { label: "Lead time médio",    value: analysis.global.leadTimeMedio, type: "days" },
    { label: "Sem marceneiro",     value: analysis.unassignedCount,    type: "number" },
  ];

  const exportSecoes = [
    {
      id: "lista",
      label: "Lista de Marceneiros",
      columns: [
        { key: "nome",         label: "Marceneiro",    type: "text" },
        { key: "ambientes",    label: "Ambientes",     type: "number" },
        { key: "concluidos",   label: "Concluídos",    type: "number" },
        { key: "emAndamento",  label: "Em execução",   type: "number" },
        { key: "atrasadosAbertos", label: "Atrasados", type: "number" },
        { key: "leadTimeMedio", label: "Lead time",    type: "days" },
        { key: "filaMedia",    label: "Fila média",    type: "days" },
        { key: "otd",          label: "OTD",           type: "percentage" },
        { key: "produtividade", label: "Produtiv.",    type: "number" },
        { key: "scoreMonitoravel", label: "Score",     type: "number" },
        { key: "valor",        label: "Receita",       type: "currency" },
      ],
      rows: filteredMarceneiros,
    },
  ];

  const exportVisual = {
    ref: pageRef,
    sections: [
      { id: "resumo",    label: "Resumo geral",         selector: "[data-pdf='resumo-marceneiros']" },
      { id: "cobertura", label: "Cobertura dos KPIs",   selector: "[data-pdf='cobertura-kpis']" },
      { id: "lista",     label: "Lista de Marceneiros", selector: "[data-pdf='lista-marceneiros']" },
    ],
  };

  useExportSnapshot(
    "marceneiros-lista",
    { titulo: "Marceneiros", resumo: exportResumo, secoes: exportSecoes, visual: exportVisual },
    pageRef
  );

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <Titulo>Marceneiros</Titulo>
        <TopBarActions>
          <BuscaInput
            type="search"
            aria-label="Busca geral em marceneiros"
            value={filters.busca}
            onChange={(event) => setFilters((current) => ({ ...current, busca: event.target.value }))}
            placeholder="Marceneiro ou situação..."
          />
          <FilterPanel
            compact
            title="Filtros de marceneiros"
            meta={`${filteredMarceneiros.length} de ${analysis.marceneiros.length} marceneiros visíveis`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Situação</FilterLabel>
                <FiltroSelect
                  value={filters.situacao}
                  onChange={(event) => setFilters((current) => ({ ...current, situacao: event.target.value }))}
                >
                  <option value="TODOS">Todas</option>
                  <option value="Com atraso">Com atraso</option>
                  <option value="Em execução">Em execução</option>
                  <option value="A iniciar">A iniciar</option>
                  <option value="Concluído">Concluído</option>
                </FiltroSelect>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          <BotaoExportacao
            titulo="Marceneiros"
            resumo={exportResumo}
            secoes={exportSecoes}
            visual={exportVisual}
            filtrosAtivos={activeFilterChips}
          />
        </TopBarActions>
      </TopBar>

      <Secao data-pdf="resumo-marceneiros">
        <SecaoTitulo>Resumo geral</SecaoTitulo>
        <Grid>
          <KPICard label="Marceneiros" valor={analysis.marceneiros.length} delay={0.03} />
          <KPICard label="Ambientes atribuídos" valor={analysis.global.ambientes} delay={0.06} />
          <KPICard
            label="OTD médio"
            valor={formatPercent(analysis.global.otd)}
            tone={getPercentTone(analysis.global.otd)}
            delay={0.09}
          />
          <KPICard
            label="Lead time médio"
            valor={formatDays(analysis.global.leadTimeMedio)}
            sub="dias produtivos sem bloqueio"
            delay={0.12}
          />
          <KPICard
            label="Sem marceneiro"
            valor={analysis.unassignedCount}
            tone={analysis.unassignedCount > 0 ? "warning" : "success"}
            delay={0.15}
          />
        </Grid>
      </Secao>

      <CoveragePanel />

      <Secao data-pdf="lista-marceneiros">
        <SecaoTitulo>Lista de marceneiros</SecaoTitulo>
        <TabelaWrapper>
          <Tabela>
            <thead>
              <tr>
                <Th>Marceneiro</Th>
                <Th>Status</Th>
                <Th $center>Amb.</Th>
                <Th $center>Concl.</Th>
                <Th $center>Exec.</Th>
                <Th $center>Lead</Th>
                <Th $center>Fila</Th>
                <Th $center>OTD</Th>
                <Th $center>Prod. proxy</Th>
                <Th $center>Score</Th>
                <Th $right>Receita</Th>
              </tr>
            </thead>
            <tbody>
              {filteredMarceneiros.length === 0 && (
                <tr>
                  <Td colSpan={11} $center>
                    Nenhum marceneiro encontrado.
                  </Td>
                </tr>
              )}
              {filteredMarceneiros.map((marceneiro, index) => {
                const situacao = getSituacaoMarceneiro(marceneiro);

                return (
                  <Tr key={marceneiro.id} $delay={index * 0.02}>
                    <Td>
                      <LinkButton type="button" onClick={() => navigate(`/marceneiros/${marceneiro.id}`)}>
                        {marceneiro.nome}
                      </LinkButton>
                    </Td>
                    <Td>
                      <Badge $tone={getSituacaoTone(situacao)}>{situacao}</Badge>
                    </Td>
                    <Td $center>{marceneiro.ambientes}</Td>
                    <Td $center>{marceneiro.concluidos}</Td>
                    <Td $center>{marceneiro.emAndamento}</Td>
                    <Td $center>{formatDays(marceneiro.leadTimeMedio)}</Td>
                    <Td $center>{formatDays(marceneiro.filaMedia)}</Td>
                    <Td $center>
                      <Badge $tone={getPercentTone(marceneiro.otd)}>{formatPercent(marceneiro.otd)}</Badge>
                    </Td>
                    <Td $center>{formatProductivity(marceneiro.produtividade)}</Td>
                    <Td $center>
                      <ScoreBar value={marceneiro.scoreMonitoravel} />
                    </Td>
                    <Td $right>{precosVisiveis ? formatarMoeda(marceneiro.valor) : "******"}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Tabela>
        </TabelaWrapper>
      </Secao>
    </Wrapper>
  );
}

function TiposTable({ rows, precosVisiveis }) {
  return (
    <TabelaWrapper>
      <Tabela>
        <thead>
          <tr>
            <Th>Tipo</Th>
            <Th $center>Qtd</Th>
            <Th $center>Concl.</Th>
            <Th $center>Lead</Th>
            <Th $center>Fila</Th>
            <Th $center>OTD</Th>
            <Th $center>Esforço</Th>
            <Th $center>Prod. proxy</Th>
            <Th $right>Receita</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td colSpan={9} $center>
                Sem tipos suficientes para análise.
              </Td>
            </tr>
          )}
          {rows.map((row, index) => (
            <Tr key={row.tipo} $delay={index * 0.025}>
              <Td>{row.tipo}</Td>
              <Td $center>{row.qtd}</Td>
              <Td $center>{row.concluidos}</Td>
              <Td $center>{formatDays(row.leadTimeMedio)}</Td>
              <Td $center>{formatDays(row.filaMedia)}</Td>
              <Td $center>
                <Badge $tone={getPercentTone(row.otd)}>{formatPercent(row.otd)}</Badge>
              </Td>
              <Td $center>{formatEffort(row.esforco)}</Td>
              <Td $center>{formatProductivity(row.produtividade)}</Td>
              <Td $right>{precosVisiveis ? formatarMoeda(row.valor) : "******"}</Td>
            </Tr>
          ))}
        </tbody>
      </Tabela>
    </TabelaWrapper>
  );
}

function AbcTable({ rows, precosVisiveis }) {
  const maxEffortShare = Math.max(1, ...rows.map((row) => row.esforcoShare || 0));
  const maxRevenueShare = Math.max(1, ...rows.map((row) => row.valorShare || 0));

  return (
    <TabelaWrapper>
      <Tabela $wide>
        <thead>
          <tr>
            <Th>Tipo</Th>
            <Th $center>ABC esforço</Th>
            <Th>Part. esforço</Th>
            <Th $center>ABC receita</Th>
            <Th>Part. receita</Th>
            <Th>Leitura</Th>
            <Th $right>Receita</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td colSpan={7} $center>
                Sem dados para curva ABC.
              </Td>
            </tr>
          )}
          {rows.map((row, index) => {
            const gap = getAbcGap(row);

            return (
              <Tr key={row.tipo} $delay={index * 0.025}>
                <Td>{row.tipo}</Td>
                <Td $center>
                  <Badge $tone={row.esforcoClass === "A" ? "warning" : "neutral"}>{row.esforcoClass}</Badge>
                </Td>
                <Td>
                  <InlineBarCell>
                    <InlineBar $width={`${((row.esforcoShare || 0) / maxEffortShare) * 100}%`} />
                    <span>{formatPercent(row.esforcoShare)}</span>
                  </InlineBarCell>
                </Td>
                <Td $center>
                  <Badge $tone={row.valorClass === "A" ? "success" : "neutral"}>{row.valorClass}</Badge>
                </Td>
                <Td>
                  <InlineBarCell>
                    <InlineBar
                      $tone="revenue"
                      $width={`${((row.valorShare || 0) / maxRevenueShare) * 100}%`}
                    />
                    <span>{formatPercent(row.valorShare)}</span>
                  </InlineBarCell>
                </Td>
                <Td>
                  <Badge $tone={gap.tone}>{gap.label}</Badge>
                </Td>
                <Td $right>{precosVisiveis ? formatarMoeda(row.valor) : "******"}</Td>
              </Tr>
            );
          })}
        </tbody>
      </Tabela>
    </TabelaWrapper>
  );
}

function AmbientesTable({ rows, precosVisiveis }) {
  return (
    <TabelaWrapper>
      <Tabela $wide>
        <thead>
          <tr>
            <Th>Cliente</Th>
            <Th>Ambiente</Th>
            <Th>Tipo</Th>
            <Th>Lote</Th>
            <Th>Início</Th>
            <Th>Término</Th>
            <Th>Prazo</Th>
            <Th $center>Lead</Th>
            <Th $center>Bloqueio</Th>
            <Th $center>Fila</Th>
            <Th $center>Status</Th>
            <Th $center>Esforço</Th>
            <Th $right>Valor</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td colSpan={13} $center>
                Nenhum ambiente atribuído.
              </Td>
            </tr>
          )}
          {rows.map((metric, index) => {
            const status = getAmbienteStatus(metric);

            return (
              <Tr key={`${metric.id}-${index}`} $delay={index * 0.015}>
                <Td>{metric.cliente}</Td>
                <Td>{metric.ambiente}</Td>
                <Td>{metric.tipo}</Td>
                <Td>{metric.lote}</Td>
                <Td>{formatarData(metric.inicio)}</Td>
                <Td>{formatarData(metric.termino)}</Td>
                <Td>{formatarData(metric.prazo)}</Td>
                <Td $center>{formatDays(metric.leadTime)}</Td>
                <Td $center>{formatDays(metric.blockDays)}</Td>
                <Td $center>{formatDays(metric.queueDays)}</Td>
                <Td $center>
                  <Badge $tone={status.tone}>{status.label}</Badge>
                </Td>
                <Td $center>{formatEffort(metric.effort)}</Td>
                <Td $right>{precosVisiveis ? formatarMoeda(metric.value) : "******"}</Td>
              </Tr>
            );
          })}
        </tbody>
      </Tabela>
    </TabelaWrapper>
  );
}

function MarceneiroDetalhe({ analysis, marceneiro }) {
  const navigate = useNavigate();
  const { precosVisiveis } = usePrecosVisiveis();
  const pageRef = useRef(null);
  const [abaAtiva, setAbaAtiva] = useState("desempenho");

  const highValueMetrics = useMemo(
    () =>
      analysis.global.metrics.filter(
        (metric) => analysis.highRevenueThreshold != null && metric.value >= analysis.highRevenueThreshold
      ),
    [analysis.global.metrics, analysis.highRevenueThreshold]
  );
  const selectedHighValue = useMemo(
    () =>
      marceneiro.metrics.filter(
        (metric) => analysis.highRevenueThreshold != null && metric.value >= analysis.highRevenueThreshold
      ),
    [marceneiro.metrics, analysis.highRevenueThreshold]
  );
  const delayedHighValue = useMemo(
    () => selectedHighValue.filter((metric) => metric.lateOpen).sort((left, right) => right.value - left.value),
    [selectedHighValue]
  );
  const dependencyShare = highValueMetrics.length
    ? (selectedHighValue.length / highValueMetrics.length) * 100
    : null;

  const exportResumoDetalhe = [
    { label: "Marceneiro",       value: marceneiro.nome,         type: "text" },
    { label: "Ambientes",        value: marceneiro.ambientes,    type: "number" },
    { label: "Concluídos",       value: marceneiro.concluidos,   type: "number" },
    { label: "Em andamento",     value: marceneiro.emAndamento,  type: "number" },
    { label: "Atrasados em aberto", value: marceneiro.atrasadosAbertos, type: "number" },
    { label: "Lead time médio",  value: marceneiro.leadTimeMedio, type: "days" },
    { label: "OTD",              value: marceneiro.otd,          type: "percentage" },
    { label: "Receita",          value: marceneiro.valor,        type: "currency" },
  ];
  const exportSecoesDetalhe = [
    {
      id: "tipos",
      label: "Tipos de ambiente",
      columns: [
        { key: "tipo",            label: "Tipo",           type: "text" },
        { key: "qtd",             label: "Qtd",            type: "number" },
        { key: "concluidos",      label: "Concluídos",     type: "number" },
        { key: "emAberto",        label: "Em aberto",      type: "number" },
        { key: "leadTimeMedio",   label: "Lead time",      type: "days" },
        { key: "otd",             label: "OTD",            type: "percentage" },
        { key: "valor",           label: "Receita",        type: "currency" },
      ],
      rows: marceneiro.tipoRows || [],
    },
  ];
  const exportVisualDetalhe = {
    ref: pageRef,
    sections: [
      { id: "desempenho", label: "Desempenho",       selector: "[data-pdf='desempenho-marceneiro']" },
      { id: "tipos",      label: "Tipos de ambiente",selector: "[data-pdf='tipos-marceneiro']" },
      { id: "economico",  label: "Econômico e fluxo",selector: "[data-pdf='economico-marceneiro']" },
      { id: "ambientes",  label: "Ambientes",        selector: "[data-pdf='ambientes-marceneiro']" },
    ],
  };

  useExportSnapshot(
    "marceneiros-detalhe",
    { titulo: `Marceneiro — ${marceneiro.nome}`, resumo: exportResumoDetalhe, secoes: exportSecoesDetalhe, visual: exportVisualDetalhe },
    pageRef
  );

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <DetailTitleBlock>
          <BackButton type="button" onClick={() => navigate("/marceneiros")}>
            <FiArrowLeft size={16} />
            Voltar
          </BackButton>
          <Titulo>{marceneiro.nome}</Titulo>
          <DetailMeta>
            {marceneiro.ambientes} ambiente{marceneiro.ambientes !== 1 ? "s" : ""} atribuído
            {marceneiro.ambientes !== 1 ? "s" : ""} | {marceneiro.concluidos} concluído
            {marceneiro.concluidos !== 1 ? "s" : ""}
          </DetailMeta>
        </DetailTitleBlock>
        <TopBarActions>
          <BotaoExportacao
            titulo={`Marceneiro — ${marceneiro.nome}`}
            resumo={exportResumoDetalhe}
            secoes={exportSecoesDetalhe}
            visual={exportVisualDetalhe}
          />
        </TopBarActions>
      </TopBar>

      <Abas>
        {ABAS_DETALHE.map((aba) => (
          <Aba key={aba.id} $active={abaAtiva === aba.id} onClick={() => setAbaAtiva(aba.id)}>
            {aba.label}
          </Aba>
        ))}
      </Abas>

      {abaAtiva === "desempenho" && (
        <Secao data-pdf="desempenho-marceneiro">
          <SecaoTitulo>Painel pessoas</SecaoTitulo>
          <Grid>
            <KPICard
              label="Lead time médio"
              valor={formatDays(marceneiro.leadTimeMedio)}
              sub="dias produtivos sem bloqueio"
              delay={0.03}
            />
            <KPICard label="Fila média antes da marcenaria" valor={formatDays(marceneiro.filaMedia)} delay={0.06} />
            <KPICard
              label="OTD interno"
              valor={formatPercent(marceneiro.otd)}
              sub={`${marceneiro.otdBase} ambiente(s) com prazo`}
              tone={getPercentTone(marceneiro.otd)}
              delay={0.09}
            />
            <KPICard
              label="Produtividade ajustada"
              valor={formatProductivity(marceneiro.produtividade)}
              sub="proxy por pontos equivalentes/dia útil"
              tone="warning"
              delay={0.12}
            />
            <KPICard
              label="Score monitorável"
              valor={marceneiro.scoreMonitoravel == null ? EMPTY_VALUE : `${formatNumber(marceneiro.scoreMonitoravel, 0)}%`}
              sub="70% da régua original coberta"
              tone={getScoreTone(marceneiro.scoreMonitoravel)}
              delay={0.15}
            />
            <KPICard
              label="Troca de contexto"
              valor={formatPercent(marceneiro.trocaContexto)}
              sub={`${marceneiro.trocaContextoQtd} ambiente(s) cruzaram semana`}
              tone={marceneiro.trocaContexto > 35 ? "warning" : "default"}
              delay={0.18}
            />
            <KPICard
              label="Disciplina de apontamento"
              valor={formatPercent(marceneiro.disciplina)}
              sub={`${marceneiro.disciplinaBase} ambiente(s) avaliados`}
              tone={getPercentTone(marceneiro.disciplina)}
              delay={0.21}
            />
            <KPICard
              label="Receita atribuída"
              valor={precosVisiveis ? formatarMoeda(marceneiro.valor) : "******"}
              delay={0.24}
            />
          </Grid>
        </Secao>
      )}

      {abaAtiva === "tipos" && (
        <Secao data-pdf="tipos-marceneiro">
          <SecaoTitulo>Painel tipos de ambiente</SecaoTitulo>
          <TiposTable rows={marceneiro.tipoRows} precosVisiveis={precosVisiveis} />
        </Secao>
      )}

      {abaAtiva === "economico" && (
        <>
          <Secao data-pdf="economico-marceneiro">
            <SecaoTitulo>Painel econômico e gargalo possível</SecaoTitulo>
            <Grid>
              <KPICard
                label="Receita por dia produtivo"
                valor={precosVisiveis && marceneiro.receitaPorDia != null ? formatarMoeda(marceneiro.receitaPorDia) : marceneiro.receitaPorDia == null ? EMPTY_VALUE : "******"}
                sub="proxy: receita concluida / dias de marcenaria"
                tone="warning"
                delay={0.03}
              />
              <KPICard
                label="Dependência em alto valor"
                valor={formatPercent(dependencyShare)}
                sub={`${selectedHighValue.length} de ${highValueMetrics.length} ambiente(s) acima da média`}
                tone={dependencyShare > 35 ? "warning" : "default"}
                delay={0.06}
              />
              <KPICard
                label="Ambientes alto valor atrasados"
                valor={delayedHighValue.length}
                tone={delayedHighValue.length > 0 ? "danger" : "success"}
                delay={0.09}
              />
              <KPICard
                label="Esforço equivalente"
                valor={formatEffort(marceneiro.esforco)}
                sub="base m2, peças e complexidade"
                delay={0.12}
              />
            </Grid>
          </Secao>

          <Secao>
            <SecaoTitulo>ABC de esforço x receita</SecaoTitulo>
            <AbcTable rows={marceneiro.abcRows} precosVisiveis={precosVisiveis} />
          </Secao>

          <Secao>
            <SecaoTitulo>Alta receita com atraso</SecaoTitulo>
            {delayedHighValue.length === 0 ? (
              <EmptyText>Nenhum ambiente de alta receita atrasado para este marceneiro.</EmptyText>
            ) : (
              <AmbientesTable rows={delayedHighValue} precosVisiveis={precosVisiveis} />
            )}
          </Secao>
        </>
      )}

      {abaAtiva === "ambientes" && (
        <Secao data-pdf="ambientes-marceneiro">
          <SecaoTitulo>Ambientes do marceneiro</SecaoTitulo>
          <AmbientesTable rows={marceneiro.metrics} precosVisiveis={precosVisiveis} />
        </Secao>
      )}
    </Wrapper>
  );
}

export default function Marceneiros() {
  const { dados, erro, initialLoading } = useDados();
  const { marceneiroId } = useParams();
  const navigate = useNavigate();

  const analysis = useMemo(() => buildMarceneiros(dados), [dados]);
  const selectedMarceneiro = useMemo(
    () => analysis.marceneiros.find((marceneiro) => marceneiro.id === marceneiroId) || null,
    [analysis.marceneiros, marceneiroId]
  );

  if (initialLoading) {
    return <PageLoader scope="marceneiros" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  if (!dados.length) {
    return <EmptyStateSemPlanilha />;
  }

  if (marceneiroId) {
    if (!selectedMarceneiro) {
      return (
        <Wrapper>
          <TopBar>
            <BackButton type="button" onClick={() => navigate("/marceneiros")}>
              <FiArrowLeft size={16} />
              Voltar
            </BackButton>
          </TopBar>
          <CoverageItem $tone="warning">
            <CoverageTitle $tone="warning">Marceneiro não encontrado</CoverageTitle>
            <DetailMeta>O nome solicitado não existe na base carregada no momento.</DetailMeta>
          </CoverageItem>
        </Wrapper>
      );
    }

    return <MarceneiroDetalhe analysis={analysis} marceneiro={selectedMarceneiro} />;
  }

  if (!analysis.marceneiros.length) {
    return (
      <Wrapper>
        <TopBar>
          <Titulo>Marceneiros</Titulo>
        </TopBar>
        <CoverageItem $tone="info">
          <CoverageTitle $tone="info">
            <FiUsers size={16} /> Nenhum marceneiro apontado
          </CoverageTitle>
          <DetailMeta>A base carregada ainda não possui ambientes com marceneiro responsável.</DetailMeta>
        </CoverageItem>
      </Wrapper>
    );
  }

  return <MarceneirosList analysis={analysis} />;
}
